// src/components/SmartLists/SmartListsWorkspaceCanvas.jsx
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Paper, Typography, CircularProgress, Alert } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

import { useAuth } from '../../contexts/AuthContext';
import { safeGetItem, safeSetItem } from '../../utils/storageUtils';

import SmartListsSheet from './SmartListsSheet';
import {
  MIN_ROWS,
  COLUMN_CONFIG,
  buildDefaultColumnWidths,
} from './smartListsGridConfig';

/**
 * SmartListsWorkspaceCanvas
 * -----------------------------------------------------------------------------
 * Controller/orchestrator for the SmartLists spreadsheet workspace.
 *
 * RESPONSIBILITIES
 *  • Own local editable grid state for each SmartList field-group
 *  • Normalize incoming SmartList option rows from the page/workspace
 *  • Pad every grouped field to the same visible row count
 *  • Manage keyboard navigation between cells
 *  • Manage focus registration for spreadsheet-like movement
 *  • Manage shared column widths + resize behavior
 *  • Persist resized widths across refreshes
 *  • Render the unified SmartListsSheet
 *
 * IMPORTANT
 *  • This component intentionally keeps edits LOCAL for now
 *  • It does NOT yet persist row edits back to Firestore
 *  • It does NOT yet push edits upstream to SmartListsPage
 *  • It is safe to iterate on the spreadsheet UX here without risking
 *    your existing OrganizationFieldsPage MVP or builder Lists workflow
 *
 * BLANK ROW BEHAVIOR
 *  • Blank rows remain visually blank
 *  • Dates/status stay empty until the row has actual option data
 *
 * FUTURE EVOLUTION
 *  • Persist canvas row edits upward via onWorkspaceGroupsChange
 *  • Add copy/paste / range selection
 *  • Add row insertion/removal actions
 *  • Add explicit row resizing
 *  • Add date validation rules and status automation
 * -----------------------------------------------------------------------------
 */

const AUTO_FIT_CHAR_PX = 8;
const AUTO_FIT_CELL_PADDING = 24;
const AUTO_FIT_MAX_WIDTH = 480;

const DEFAULT_ROW_APPEND_COUNT = 50;
const DEFAULT_DEFINED_OPTION_LIFECYCLE_STATUS = 'active';

const buildBlankRow = () => ({
  rowId: `smartlists-row-${uuidv4()}`,
  optionId: '',
  label: '',
  value: '',
  hint: '',
  lifecycleStatus: '',
  liveFrom: '',
  liveTo: '',
  isPendingRequest: false,
  requestId: null,
  dirty: false,
  isNew: true,
  isDeleted: false,
  isBlankRow: true,
});

const normalizeDateString = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;

  try {
    if (typeof value?.toDate === 'function') {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime())
        ? d.toISOString().slice(0, 10)
        : '';
    }
  } catch (error) {
    // no-op
  }

  return '';
};

const hasDefaultLifecycleOnlyBecauseOptionExists = (row = {}) => {
  const normalizedLifecycleStatus = String(row?.lifecycleStatus || '').trim().toLowerCase();
  const normalizedLabel = String(row?.label || '').trim();

  return normalizedLifecycleStatus === DEFAULT_DEFINED_OPTION_LIFECYCLE_STATUS && !!normalizedLabel;
};

/**
 * Reset a row back to a true blank spreadsheet row.
 *
 * IMPORTANT
 *  • This is used when the user clears the option label and the row no longer
 *    contains any real user-entered content
 *  • We intentionally clear:
 *      - optionId
 *      - launch date
 *      - end date
 *      - hint
 *      - value
 *      - lifecycleStatus
 *  • The row remains visually available to the user, but it will not be saved
 */
const resetRowToBlankState = (row = {}) => ({
  rowId: row?.rowId || `smartlists-row-${uuidv4()}`,
  optionId: '',
  label: '',
  value: '',
  hint: '',
  lifecycleStatus: '',
  liveFrom: '',
  liveTo: '',
  isPendingRequest: false,
  requestId: null,
  dirty: true,
  isNew: true,
  isDeleted: false,
  isBlankRow: true,
});

const rowHasMeaningfulContent = (row = {}) => {
  return Boolean(
    row?.label ||
    row?.hint ||
    row?.liveFrom ||
    row?.liveTo ||
    row?.optionId ||
    (
      row?.lifecycleStatus &&
      !hasDefaultLifecycleOnlyBecauseOptionExists(row)
    )
  );
};

const normalizeGroupRows = (rows = []) => {
  const normalized = Array.isArray(rows)
    ? rows
        .filter((row) => !row?.isDeleted)
        .map((row) => {
          const normalizedLabel = String(row?.label || '').trim();
          const normalizedLifecycleStatus = (() => {
            const incoming = String(row?.lifecycleStatus || '').trim().toLowerCase();

            // IMPORTANT:
            // - blank incoming rows stay blank visually
            // - rows with an actual option but no status normalize to active
            if (!incoming) {
              return normalizedLabel ? DEFAULT_DEFINED_OPTION_LIFECYCLE_STATUS : '';
            }

            return incoming;
          })();

          const normalizedRow = {
            rowId: row?.rowId || `smartlists-row-${uuidv4()}`,
            optionId: row?.optionId || '',
            label: row?.label || '',
            value: row?.value || '',
            hint: row?.hint || '',
            lifecycleStatus: normalizedLifecycleStatus,
            liveFrom: normalizeDateString(row?.liveFrom),
            liveTo: normalizeDateString(row?.liveTo),
            isPendingRequest: row?.isPendingRequest === true,
            requestId: row?.requestId || null,
            dirty: row?.dirty === true,
            isNew: row?.isNew === true,
            isDeleted: false,
          };

          return {
            ...normalizedRow,
            isBlankRow: !rowHasMeaningfulContent(normalizedRow),
          };
        })
    : [];

  return normalized;
};

/**
 * Build a clean outgoing row snapshot from the local canvas grid.
 *
 * IMPORTANT
 *  • This is NOT used for live cell rendering
 *  • This is used only when the parent explicitly asks the canvas
 *    for the latest workspace snapshot (for example on Save)
 *  • We intentionally drop padded blank rows here so the parent/service
 *    layer does not receive spreadsheet-only padding noise
 *
 * WHY THIS MATTERS
 *  • localGridByGroup remains free to over-allocate rows for UX
 *  • canonical Save should consume meaningful row state, not rendered padding
 */
const buildOutgoingGroupRows = (rows = []) => {
  const normalizedRows = normalizeGroupRows(rows);

  return normalizedRows
    .filter((row) => row?.isDeleted === true || rowHasMeaningfulContent(row))
    .map((row) => ({
      rowId: row?.rowId || `smartlists-row-${uuidv4()}`,
      optionId: row?.optionId || '',
      label: row?.label || '',
      value: row?.value || '',
      hint: row?.hint || '',
      lifecycleStatus: row?.label
        ? (row?.lifecycleStatus || DEFAULT_DEFINED_OPTION_LIFECYCLE_STATUS)
        : '',
      liveFrom: normalizeDateString(row?.liveFrom),
      liveTo: normalizeDateString(row?.liveTo),
      isPendingRequest: row?.isPendingRequest === true,
      requestId: row?.requestId || null,
      dirty: row?.dirty === true,
      isNew: row?.isNew === true,
      isDeleted: row?.isDeleted === true,
      isBlankRow: false,
    }));
};

const padRows = (rows, targetLength) => {
  const next = [...rows];
  const needed = Math.max(targetLength - next.length, 0);

  for (let i = 0; i < needed; i += 1) {
    next.push(buildBlankRow());
  }

  return next;
};

const measureTextWidthRoughly = (text = '') => {
  const normalized = String(text || '');
  return normalized.length * AUTO_FIT_CHAR_PX + AUTO_FIT_CELL_PADDING;
};

const getStatusLabel = (value) => {
  if (value === 'active') return 'Active';
  if (value === 'inactive') return 'Inactive';
  if (value === 'pending') return 'Pending';
  if (value === 'archived') return 'Archive';
  return '';
};

const normalizePastedStatusValue = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();

  // IMPORTANT:
  // Pasted blank status should stay blank at the spreadsheet/UI level.
  // If a row has a real option and no status, we will normalize it to active
  // later at the row model / save boundary.
  if (!normalized) return '';

  if (normalized === 'active') return 'active';
  if (normalized === 'inactive') return 'inactive';
  if (normalized === 'pending') return 'pending';
  if (normalized === 'archive' || normalized === 'archived') return 'archived';

  return '';
};

const normalizeSlashDateToIso = (value = '') => {
  const trimmed = String(value || '').trim();
  const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+.*)?$/);

  if (!match) return null;

  const [, mmRaw, ddRaw, yyyyRaw] = match;
  const mm = Number(mmRaw);
  const dd = Number(ddRaw);
  const yyyy = Number(yyyyRaw);

  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yyyy < 1000) {
    return null;
  }

  const candidate = new Date(Date.UTC(yyyy, mm - 1, dd));

  if (
    candidate.getUTCFullYear() !== yyyy ||
    candidate.getUTCMonth() !== mm - 1 ||
    candidate.getUTCDate() !== dd
  ) {
    return null;
  }

  return `${String(yyyy).padStart(4, '0')}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
};

const normalizePastedDateValue = (value = '') => {
  const trimmed = String(value || '').trim();

  if (!trimmed) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  const slashIso = normalizeSlashDateToIso(trimmed);
  if (slashIso) {
    return slashIso;
  }

  // Preserve the raw text if we do not recognize the format yet.
  // This avoids silent data loss and keeps future validation possible.
  return trimmed;
};

const normalizePastedCellValue = (colKey, value) => {
  if (colKey === 'lifecycleStatus') {
    return normalizePastedStatusValue(value);
  }

  if (colKey === 'liveFrom' || colKey === 'liveTo') {
    return normalizePastedDateValue(value);
  }

  return String(value ?? '').replace(/\r/g, '');
};

const parseClipboardTextToGrid = (text = '') => {
  const normalizedText = String(text ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');

  const parsedRows = normalizedText.split('\n').map((line) => line.split('\t'));

  // Google Sheets / Excel clipboard payloads often end with one trailing empty row.
  // Remove only the terminal empty rows so we preserve interior blank lines.
  while (
    parsedRows.length > 0 &&
    parsedRows[parsedRows.length - 1].every((cell) => cell === '')
  ) {
    parsedRows.pop();
  }

  return parsedRows;
};

const SmartListsWorkspaceCanvas = forwardRef(({
  pageLoading = false,
  pageError = null,
  visibleColumnGroups = [],
  selectedDraftId = null,
  onSelectField,
  onMarkGroupDirty = null,
}, ref) => {
  const { organizationId } = useAuth() || {};

  const tableRef = useRef(null);
  const cellRefs = useRef({});
  const resizeStateRef = useRef(null);

  const promotedDirtyGroupIdsRef = useRef(new Set());

  const [localGridByGroup, setLocalGridByGroup] = useState({});
  const [selectedCell, setSelectedCell] = useState({
    groupId: null,
    rowIndex: null,
    colKey: null,
  });

    /**
   * Visible row count control
   * ---------------------------------------------------------------------------
   * This is the controller-side source of truth for how many rows the sheet
   * should visibly render.
   *
   * WHY THIS EXISTS
   *  • MIN_ROWS establishes the default baseline for a new SmartLists sheet
   *  • users can explicitly add more rows from the footer UI
   *  • incoming real data may already exceed the current requested row count
   *
   * RULES
   *  • never render fewer than MIN_ROWS
   *  • never render fewer than the largest incoming group row count
   *  • when the footer requests more rows, increase the visible baseline
   *
   * PERSISTENCE
   *  • the requested visible row count should survive refreshes
   *  • we therefore hydrate from storage when organizationId is available
   * ------------------------------------------------------------------------- */
  const [requestedVisibleRowCount, setRequestedVisibleRowCount] = useState(() => {
    if (!organizationId) return MIN_ROWS;

    try {
      const stored = safeGetItem(`org_${organizationId}_smartlists_visibleRowCount`);
      const parsed = Number(stored);

      return Number.isFinite(parsed) && parsed >= MIN_ROWS
        ? parsed
        : MIN_ROWS;
    } catch (error) {
      return MIN_ROWS;
    }
  });
  

  const columnWidthsStorageKey = useMemo(() => {
    return organizationId
      ? `org_${organizationId}_smartlists_columnWidths`
      : null;
  }, [organizationId]);

  const visibleRowCountStorageKey = useMemo(() => {
    return organizationId
      ? `org_${organizationId}_smartlists_visibleRowCount`
      : null;
  }, [organizationId]);

  const [columnWidths, setColumnWidths] = useState(() => {
    const defaults = buildDefaultColumnWidths();

    if (!organizationId) return defaults;

    try {
      const stored = safeGetItem(`org_${organizationId}_smartlists_columnWidths`);
      return stored && typeof stored === 'object'
        ? { ...defaults, ...stored }
        : defaults;
    } catch (error) {
      return defaults;
    }
  });

  /**
   * Imperative workspace snapshot API
   * ---------------------------------------------------------------------------
   * WHY THIS EXISTS
   *  • The canvas is the hot local editing owner and must remain fast
   *  • We do NOT want to push every keystroke upward into SmartListsPage
   *    because that would create parent churn and risk typing lag
   *  • Instead, the page can explicitly pull the latest local grid snapshot
   *    at Save time (or other intentional checkpoints)
   *
   * WHAT THIS RETURNS
   *  • The same grouped workspace shape the page already understands
   *  • Each group's `optionRows` is rebuilt from the local canvas state
   *  • Padded blank rows are removed from the outgoing snapshot
   */
const buildWorkspaceSnapshot = useCallback(() => {
  return (visibleColumnGroups || []).map((group, index) => {
    const groupId = group?.localDraftId;
    const localRows = Array.isArray(localGridByGroup?.[groupId])
      ? localGridByGroup[groupId]
      : (Array.isArray(group?.optionRows) ? group.optionRows : []);

    return {
      ...group,
      order: group.order ?? index,  // <-- preserve or assign sequential order
      optionRows: buildOutgoingGroupRows(localRows),
    };
  });
}, [visibleColumnGroups, localGridByGroup]);

  useImperativeHandle(ref, () => ({
    getWorkspaceSnapshot: () => {
      const snapshot = buildWorkspaceSnapshot();
      console.log('[SmartListsWorkspaceCanvas] getWorkspaceSnapshot called.', {
        groupCount: snapshot.length,
        groupIds: snapshot.map((group) => group?.localDraftId || null),
      });

      return snapshot;
    },
  }), [buildWorkspaceSnapshot]);

  /**
   * Promote "dirty" state upward without pushing the whole worksheet upward.
   *
   * IMPORTANT
   *  • This is intentionally lightweight
   *  • A group is promoted to dirty only once until upstream resets it
   *  • This avoids parent churn on every keystroke while still allowing
   *    page-level dirty UX to react promptly
   */
  const markGroupDirty = useCallback((groupId) => {
    if (!groupId || typeof onMarkGroupDirty !== 'function') return;

    if (promotedDirtyGroupIdsRef.current.has(groupId)) {
      return;
    }

    promotedDirtyGroupIdsRef.current.add(groupId);

    console.log('[SmartListsWorkspaceCanvas] Promoting group dirty state upward:', {
      groupId,
    });

    onMarkGroupDirty(groupId);
  }, [onMarkGroupDirty]);

  useEffect(() => {
    if (!columnWidthsStorageKey) return;
    safeSetItem(columnWidthsStorageKey, columnWidths);
  }, [columnWidthsStorageKey, columnWidths]);

  useEffect(() => {
    if (!visibleRowCountStorageKey) return;

    safeSetItem(visibleRowCountStorageKey, requestedVisibleRowCount);
  }, [visibleRowCountStorageKey, requestedVisibleRowCount]);

  useEffect(() => {
    const nextDirtyIds = new Set(
      (visibleColumnGroups || [])
        .filter((group) => group?.isDirty === true || group?.isNew === true)
        .map((group) => group?.localDraftId)
        .filter(Boolean)
    );

    promotedDirtyGroupIdsRef.current = nextDirtyIds;
  }, [visibleColumnGroups]);

  useEffect(() => {
    setLocalGridByGroup((prev) => {
      const next = { ...prev };
      const incomingGroupIds = new Set();

      (visibleColumnGroups || []).forEach((group) => {
        const groupId = group?.localDraftId;
        if (!groupId) return;

        incomingGroupIds.add(groupId);

        // IMPORTANT:
        // Preserve existing local grid state when this group is already being
        // edited in the canvas. This keeps local typing/snappy spreadsheet UX
        // as the source of truth and prevents parent re-renders from blowing
        // away in-progress local edits.
        if (Array.isArray(prev[groupId])) {
          return;
        }

        next[groupId] = normalizeGroupRows(group?.optionRows || []);
      });

      // Remove groups that no longer exist upstream
      Object.keys(next).forEach((groupId) => {
        if (!incomingGroupIds.has(groupId)) {
          delete next[groupId];
        }
      });

      return next;
    });
  }, [visibleColumnGroups]);

  useEffect(() => {
    const largestIncomingGroup = (visibleColumnGroups || []).reduce((acc, group) => {
      const count = Array.isArray(group?.optionRows) ? group.optionRows.length : 0;
      return Math.max(acc, count);
    }, 0);

    setRequestedVisibleRowCount((prev) => Math.max(prev, largestIncomingGroup, MIN_ROWS));
  }, [visibleColumnGroups]);

  const maxVisibleRows = useMemo(() => {
    const maxIncoming = (visibleColumnGroups || []).reduce((acc, group) => {
      const groupId = group?.localDraftId;
      const count = Array.isArray(localGridByGroup[groupId])
        ? localGridByGroup[groupId].length
        : (Array.isArray(group?.optionRows) ? group.optionRows.length : 0);

      return Math.max(acc, count);
    }, 0);

    return Math.max(maxIncoming, requestedVisibleRowCount, MIN_ROWS);
  }, [visibleColumnGroups, localGridByGroup, requestedVisibleRowCount]);

  const paddedGroups = useMemo(() => {
    return (visibleColumnGroups || []).map((group) => {
      const groupId = group?.localDraftId;
      const sourceRows =
        localGridByGroup[groupId] || normalizeGroupRows(group?.optionRows || []);

      return {
        ...group,
        renderedRows: padRows(sourceRows, maxVisibleRows),
      };
    });
  }, [visibleColumnGroups, localGridByGroup, maxVisibleRows]);

  const orderedGroupIds = useMemo(() => {
    return paddedGroups.map((group) => group.localDraftId);
  }, [paddedGroups]);

  const columnKeyIndexMap = useMemo(() => {
    return COLUMN_CONFIG.reduce((acc, column, index) => {
      acc[column.key] = index;
      return acc;
    }, {});
  }, []);

  const resolvePasteTargetCell = useCallback((startGroupId, startColKey, colOffset) => {
    const startGroupIndex = orderedGroupIds.indexOf(startGroupId);
    const startColIndex = columnKeyIndexMap[startColKey];

    if (startGroupIndex < 0 || typeof startColIndex !== 'number') {
      return null;
    }

    const totalColumnsPerGroup = COLUMN_CONFIG.length;
    const absoluteColumnIndex =
      (startGroupIndex * totalColumnsPerGroup) + startColIndex + colOffset;

    if (absoluteColumnIndex < 0) {
      return null;
    }

    const targetGroupIndex = Math.floor(absoluteColumnIndex / totalColumnsPerGroup);
    const targetColumnIndex = absoluteColumnIndex % totalColumnsPerGroup;

    if (targetGroupIndex < 0 || targetGroupIndex >= orderedGroupIds.length) {
      return null;
    }

    const targetColumn = COLUMN_CONFIG[targetColumnIndex];
    if (!targetColumn) {
      return null;
    }

    return {
      groupId: orderedGroupIds[targetGroupIndex],
      colKey: targetColumn.key,
    };
  }, [orderedGroupIds, columnKeyIndexMap]);

  const getCellRefKey = useCallback((groupId, rowIndex, colKey) => {
    return `${groupId}__${rowIndex}__${colKey}`;
  }, []);

  const registerCellRef = useCallback((groupId, rowIndex, colKey, node) => {
    const key = getCellRefKey(groupId, rowIndex, colKey);

    if (node) {
      cellRefs.current[key] = node;
    } else {
      delete cellRefs.current[key];
    }
  }, [getCellRefKey]);

  const focusCell = useCallback((groupId, rowIndex, colKey) => {
    const key = getCellRefKey(groupId, rowIndex, colKey);
    const node = cellRefs.current[key];

    if (node && typeof node.focus === 'function') {
      node.focus();
      setSelectedCell({ groupId, rowIndex, colKey });

      try {
        if (typeof node.select === 'function') {
          node.select();
        }
      } catch (error) {
        // no-op
      }
    }
  }, [getCellRefKey]);

  const getNextHorizontalTarget = useCallback((groupId, colKey, direction = 1) => {
    const currentGroupIndex = orderedGroupIds.indexOf(groupId);
    const currentColIndex = COLUMN_CONFIG.findIndex((col) => col.key === colKey);

    if (currentGroupIndex < 0 || currentColIndex < 0) {
      return null;
    }

    let nextGroupIndex = currentGroupIndex;
    let nextColIndex = currentColIndex + direction;

    if (nextColIndex >= COLUMN_CONFIG.length) {
      nextGroupIndex += 1;
      nextColIndex = 0;
    } else if (nextColIndex < 0) {
      nextGroupIndex -= 1;
      nextColIndex = COLUMN_CONFIG.length - 1;
    }

    if (nextGroupIndex < 0 || nextGroupIndex >= orderedGroupIds.length) {
      return null;
    }

    return {
      groupId: orderedGroupIds[nextGroupIndex],
      colKey: COLUMN_CONFIG[nextColIndex].key,
    };
  }, [orderedGroupIds]);

  const handleMoveSelection = useCallback((groupId, rowIndex, colKey, direction) => {
    if (direction === 'up') {
      const nextRow = Math.max(rowIndex - 1, 0);
      focusCell(groupId, nextRow, colKey);
      return;
    }

    if (direction === 'down') {
      const nextRow = Math.min(rowIndex + 1, maxVisibleRows - 1);
      focusCell(groupId, nextRow, colKey);
      return;
    }

    if (direction === 'left') {
      const next = getNextHorizontalTarget(groupId, colKey, -1);
      if (next) {
        focusCell(next.groupId, rowIndex, next.colKey);
      }
      return;
    }

    if (direction === 'right') {
      const next = getNextHorizontalTarget(groupId, colKey, 1);
      if (next) {
        focusCell(next.groupId, rowIndex, next.colKey);
      }
    }
  }, [focusCell, getNextHorizontalTarget, maxVisibleRows]);

const updateCellValue = useCallback((groupId, rowIndex, colKey, value) => {
  markGroupDirty(groupId);

  setLocalGridByGroup((prev) => {
    const existingRows = Array.isArray(prev[groupId]) ? [...prev[groupId]] : [];
    const padded = padRows(existingRows, Math.max(existingRows.length, rowIndex + 1, MIN_ROWS));

    const currentRow = padded[rowIndex] || buildBlankRow();

    let nextValue = value;

    if (colKey === 'lifecycleStatus') {
      const normalizedLifecycleValue = String(value || '').trim().toLowerCase();
      const allowed = ['active', 'inactive', 'pending', 'archived'];

      nextValue = allowed.includes(normalizedLifecycleValue)
        ? normalizedLifecycleValue
        : '';
    }

    const nextRow = {
      ...currentRow,
      [colKey]: nextValue,
      dirty: true,
    };

    const normalizedLabel = String(
      colKey === 'label' ? nextValue : nextRow?.label || ''
    ).trim();

    // Once the user defines an option, default status to active if it was blank
    if (normalizedLabel && !nextRow.lifecycleStatus) {
      nextRow.lifecycleStatus = DEFAULT_DEFINED_OPTION_LIFECYCLE_STATUS;
    }

    if (colKey === 'label' && normalizedLabel && !nextRow.optionId) {
      nextRow.optionId = uuidv4();
    }

    // ---------------------------------------------------------------------
    // Enterprise blank-row cleanup rule
    // ---------------------------------------------------------------------
    // If the user clears the option label and the row no longer contains
    // any real user-entered content, normalize it back to a true blank row.
    //
    // IMPORTANT
    //  • This clears stale dates/status/optionId from a previously edited row
    //  • This ensures blank rows do not accidentally persist on Save
    //  • This remains local-only and does not affect typing performance
    // ---------------------------------------------------------------------
    if (!normalizedLabel) {
      const candidateBlankRow = {
        ...nextRow,
        optionId: '',
        value: '',
        hint: '',
        liveFrom: '',
        liveTo: '',
        lifecycleStatus: '',
      };

      if (!rowHasMeaningfulContent(candidateBlankRow)) {
        const resetRow = resetRowToBlankState(candidateBlankRow);
        padded[rowIndex] = resetRow;

        const isLastRow = rowIndex === padded.length - 1;
        const rowHasAnyContent = rowHasMeaningfulContent(resetRow);

        let nextRows = padded;
        if (isLastRow && rowHasAnyContent) {
          nextRows = [...padded, buildBlankRow()];
        }

        return {
          ...prev,
          [groupId]: nextRows,
        };
      }
    }

    nextRow.isBlankRow = !rowHasMeaningfulContent(nextRow);
    padded[rowIndex] = nextRow;

    const isLastRow = rowIndex === padded.length - 1;
    const rowHasAnyContent = rowHasMeaningfulContent(nextRow);

    let nextRows = padded;
    if (isLastRow && rowHasAnyContent) {
      nextRows = [...padded, buildBlankRow()];
    }

    return {
      ...prev,
      [groupId]: nextRows,
    };
  });
}, [markGroupDirty]);

  const handleCellPaste = useCallback((event, groupId, rowIndex, colKey) => {
    const clipboardText = event.clipboardData?.getData('text/plain') || '';
    const parsedGrid = parseClipboardTextToGrid(clipboardText);

    if (!clipboardText || parsedGrid.length === 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    let lastWritableTarget = null;

    parsedGrid.forEach((pastedRow, rowOffset) => {
      pastedRow.forEach((_, colOffset) => {
        const resolvedTarget = resolvePasteTargetCell(groupId, colKey, colOffset);
        if (!resolvedTarget) return;

        lastWritableTarget = {
          groupId: resolvedTarget.groupId,
          rowIndex: rowIndex + rowOffset,
          colKey: resolvedTarget.colKey,
        };
      });
    });

    console.log('[SmartListsWorkspaceCanvas] Applying pasted grid:', {
      startGroupId: groupId,
      startRowIndex: rowIndex,
      startColKey: colKey,
      pastedRowCount: parsedGrid.length,
      pastedColumnCount: Math.max(...parsedGrid.map((row) => row.length), 0),
      lastWritableTarget,
    });

    setLocalGridByGroup((prev) => {
      const next = { ...prev };
      const workingRowsByGroup = {};
      const touchedGroupIds = new Set();

      const ensureWritableRows = (targetGroupId, targetRowIndex) => {
        const existingRows = Array.isArray(workingRowsByGroup[targetGroupId])
          ? workingRowsByGroup[targetGroupId]
          : (Array.isArray(next[targetGroupId]) ? [...next[targetGroupId]] : []);

        const paddedRows = padRows(
          existingRows,
          Math.max(existingRows.length, targetRowIndex + 1, MIN_ROWS)
        );

        workingRowsByGroup[targetGroupId] = paddedRows;
        return paddedRows;
      };

      parsedGrid.forEach((pastedRow, rowOffset) => {
        pastedRow.forEach((rawValue, colOffset) => {
          const resolvedTarget = resolvePasteTargetCell(groupId, colKey, colOffset);
          if (!resolvedTarget) return;

          const targetRowIndex = rowIndex + rowOffset;
          const targetGroupId = resolvedTarget.groupId;
          const targetColKey = resolvedTarget.colKey;

          const writableRows = ensureWritableRows(targetGroupId, targetRowIndex);
          const currentRow = writableRows[targetRowIndex] || buildBlankRow();
          const normalizedValue = normalizePastedCellValue(targetColKey, rawValue);

          const nextRow = {
            ...currentRow,
            [targetColKey]: normalizedValue,
            dirty: true,
          };

          const normalizedLabel = String(
            targetColKey === 'label' ? normalizedValue : nextRow?.label || ''
          ).trim();

          if (normalizedLabel && !nextRow.lifecycleStatus) {
            nextRow.lifecycleStatus = DEFAULT_DEFINED_OPTION_LIFECYCLE_STATUS;
          }

          if (targetColKey === 'label' && normalizedValue && !nextRow.optionId) {
            nextRow.optionId = uuidv4();
          }

          // If pasted content results in an effectively blank row, normalize it
          // back to the true blank row model so it does not persist accidentally.
          if (!normalizedLabel) {
            const candidateBlankRow = {
              ...nextRow,
              optionId: '',
              value: '',
              hint: '',
              liveFrom: '',
              liveTo: '',
              lifecycleStatus: '',
            };

            if (!rowHasMeaningfulContent(candidateBlankRow)) {
              writableRows[targetRowIndex] = resetRowToBlankState(candidateBlankRow);
              touchedGroupIds.add(targetGroupId);
              return;
            }
          }

          nextRow.isBlankRow = !rowHasMeaningfulContent(nextRow);
          writableRows[targetRowIndex] = nextRow;
          touchedGroupIds.add(targetGroupId);
        });
      });

      touchedGroupIds.forEach((targetGroupId) => {
        let nextRows = Array.isArray(workingRowsByGroup[targetGroupId])
          ? [...workingRowsByGroup[targetGroupId]]
          : (Array.isArray(next[targetGroupId]) ? [...next[targetGroupId]] : []);

        nextRows = nextRows.map((row) => ({
          ...row,
          isBlankRow: !rowHasMeaningfulContent(row),
        }));

        const lastMeaningfulRowIndex = nextRows.reduce((acc, row, index) => {
          return rowHasMeaningfulContent(row) ? index : acc;
        }, -1);

        const lastRow = nextRows[nextRows.length - 1] || null;
        const hasTrailingBlankRow = lastRow?.isBlankRow === true;

        if (lastMeaningfulRowIndex === nextRows.length - 1 && !hasTrailingBlankRow) {
          nextRows.push(buildBlankRow());
        }

        next[targetGroupId] = nextRows;
      });

      return next;
    });

    (Array.from(
      new Set(
        parsedGrid.flatMap((pastedRow, rowOffset) =>
          pastedRow.map((_, colOffset) => {
            const resolvedTarget = resolvePasteTargetCell(groupId, colKey, colOffset);
            return resolvedTarget?.groupId || null;
          })
        )
      )
    ).filter(Boolean)).forEach((dirtyGroupId) => {
      markGroupDirty(dirtyGroupId);
    });

    if (lastWritableTarget) {
      setSelectedCell(lastWritableTarget);

      requestAnimationFrame(() => {
        focusCell(
          lastWritableTarget.groupId,
          lastWritableTarget.rowIndex,
          lastWritableTarget.colKey
        );
      });
    }
  }, [focusCell, resolvePasteTargetCell, markGroupDirty]);

  const handleCellKeyDown = useCallback((event, groupId, rowIndex, colKey) => {
    const { key, shiftKey } = event;

    if (key === 'ArrowUp') {
      event.preventDefault();
      handleMoveSelection(groupId, rowIndex, colKey, 'up');
      return;
    }

    if (key === 'ArrowDown') {
      event.preventDefault();
      handleMoveSelection(groupId, rowIndex, colKey, 'down');
      return;
    }

    if (key === 'ArrowLeft') {
      if (event.target.selectionStart === 0 && event.target.selectionEnd === 0) {
        event.preventDefault();
        handleMoveSelection(groupId, rowIndex, colKey, 'left');
      }
      return;
    }

    if (key === 'ArrowRight') {
      const target = event.target;
      const atEnd =
        typeof target.selectionStart === 'number' &&
        typeof target.value === 'string' &&
        target.selectionStart === target.value.length &&
        target.selectionEnd === target.value.length;

      if (atEnd || target.tagName === 'SELECT') {
        event.preventDefault();
        handleMoveSelection(groupId, rowIndex, colKey, 'right');
      }
      return;
    }

    if (key === 'Tab') {
      event.preventDefault();
      handleMoveSelection(groupId, rowIndex, colKey, shiftKey ? 'left' : 'right');
      return;
    }

    if (key === 'Enter') {
      event.preventDefault();
      handleMoveSelection(groupId, rowIndex, colKey, shiftKey ? 'up' : 'down');
    }
  }, [handleMoveSelection]);

  const handleSelectCell = useCallback((groupId, rowIndex, colKey) => {
    setSelectedCell({ groupId, rowIndex, colKey });
  }, []);

  /**
   * Footer-driven row growth
   * ---------------------------------------------------------------------------
   * This supports the Google-Sheets-inspired bottom footer:
   *   Add [50] more rows at the bottom
   *
   * We intentionally grow the visible row baseline instead of mutating every
   * group's underlying row array immediately. The actual row data remains
   * sparse/local until the user edits or pastes into those rows.
   * ------------------------------------------------------------------------- */
  const handleAddRows = useCallback((count = DEFAULT_ROW_APPEND_COUNT) => {
    const safeCount = Number.isFinite(count) && count > 0 ? count : DEFAULT_ROW_APPEND_COUNT;

    setRequestedVisibleRowCount((prev) => {
      const next = prev + safeCount;

      console.log('[SmartListsWorkspaceCanvas] Added more visible rows:', {
        addedCount: safeCount,
        previousRequestedVisibleRowCount: prev,
        nextRequestedVisibleRowCount: next,
      });

      return next;
    });
  }, []);

  const handleStartResize = useCallback((event, colKey) => {
    event.preventDefault();
    event.stopPropagation();

    resizeStateRef.current = {
      colKey,
      startX: event.clientX,
      startWidth: columnWidths[colKey],
    };
  }, [columnWidths]);

  const handleAutoFitColumn = useCallback((colKey) => {
    const columnDef = COLUMN_CONFIG.find((col) => col.key === colKey);
    if (!columnDef) return;

    let widest = measureTextWidthRoughly(columnDef.label);

    paddedGroups.forEach((group) => {
      const rows = Array.isArray(group?.renderedRows) ? group.renderedRows : [];

      rows.forEach((row) => {
        if (row?.isBlankRow) return;

        let displayValue = row?.[colKey] ?? '';

        if (colKey === 'lifecycleStatus') {
          displayValue = getStatusLabel(displayValue);
        }

        widest = Math.max(widest, measureTextWidthRoughly(displayValue));
      });
    });

    const nextWidth = Math.max(
      columnDef.minWidth || 48,
      Math.min(widest, AUTO_FIT_MAX_WIDTH)
    );

    setColumnWidths((prev) => ({
      ...prev,
      [colKey]: nextWidth,
    }));
  }, [paddedGroups]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!resizeStateRef.current) return;

      const { colKey, startX, startWidth } = resizeStateRef.current;
      const columnDef = COLUMN_CONFIG.find((col) => col.key === colKey);
      if (!columnDef) return;

      const delta = event.clientX - startX;
      const nextWidth = Math.max(columnDef.minWidth || 48, startWidth + delta);

      setColumnWidths((prev) => ({
        ...prev,
        [colKey]: nextWidth,
      }));
    };

    const handleMouseUp = () => {
      resizeStateRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  if (pageLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          overflow: 'hidden',
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (pageError) {
    return (
      <Box
        sx={{
          flex: 1,
          p: 0,
          backgroundColor: '#ffffff',
          overflow: 'hidden',
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <Alert severity="error" sx={{ m: 0, borderRadius: 0 }}>
          {pageError}
        </Alert>
      </Box>
    );
  }

  if (!Array.isArray(visibleColumnGroups) || visibleColumnGroups.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          p: 0,
          backgroundColor: '#ffffff',
          overflow: 'hidden',
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: '1px dashed #cfd8dc',
            backgroundColor: '#fff',
            minWidth: '100%',
            minHeight: '100%',
            borderRadius: 0,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            SmartLists
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This is the SmartLists workspace canvas. Click the + button in the sidebar to create a
            new local draft organization field. The new field will appear as the leftmost grouped
            column set here.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        /**
         * Canvas pane boundary
         * -------------------------------------------------------------------
         * This outer container participates in the split-pane page layout only.
         * It should NOT also be responsible for rendering the actual scrolling
         * viewport for the spreadsheet content.
         *
         * By separating the pane boundary from the scroll viewport, we create a
         * cleaner ownership model:
         *   • outer Box   -> layout participant in the right pane
         *   • inner Box   -> dedicated scroll viewport for the canvas
         *   • Paper/Sheet -> natural content inside that viewport
         *
         * This is the source-level fix for sidebar/canvas scroll interference.
         * ----------------------------------------------------------------- */
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        ref={tableRef}
        sx={{
          /**
           * Dedicated canvas viewport
           * -----------------------------------------------------------------
           * This inner container is now the ONLY scroll owner for spreadsheet
           * interaction in the right pane.
           *
           * Because it lives inside a bounded outer pane container, the canvas
           * can scroll independently without dragging sibling regions such as
           * the SmartLists sidepanel along with it.
           * ----------------------------------------------------------------- */
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflowX: 'auto',
          overflowY: 'auto',
          backgroundColor: '#ffffff',
          position: 'relative',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: 'fit-content',
            minWidth: '100%',
            border: '1px solid #e0e0e0',
            backgroundColor: '#fff',

            // IMPORTANT:
            // Let sticky cells remain visible while the inner viewport handles scrolling.
            overflow: 'visible',

            position: 'relative',
          }}
        >
          <SmartListsSheet
            groups={paddedGroups}
            rowCount={maxVisibleRows}
            selectedDraftId={selectedDraftId}
            selectedCell={selectedCell}
            registerCellRef={registerCellRef}
            onSelectCell={handleSelectCell}
            onChangeValue={updateCellValue}
            onKeyDown={handleCellKeyDown}
            onPaste={handleCellPaste}
            onSelectField={onSelectField}
            columnWidths={columnWidths}
            onStartResize={handleStartResize}
            onAutoFitColumn={handleAutoFitColumn}
            onAddRows={handleAddRows}
            defaultAddRowsCount={DEFAULT_ROW_APPEND_COUNT}
          />
        </Paper>
      </Box>
    </Box>
  );
});

export default SmartListsWorkspaceCanvas;